import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
  type ChangeEvent,
} from 'react';

import { useDebounce } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { useAtomValue } from 'jotai';

import { useFileSystemNodes } from '../../../hooks/useFileSystemNodes.js';
import { useFolderNavigation } from '../../../hooks/useFolderNavigation.js';
import { folderPathAtom } from '../../../store/fileSystem.js';
import {
  ROOT_FOLDER_ID,
  type FileNode,
  type FileSystemNode,
  type FolderCrumb,
  type FolderNode,
} from '../../../types/fileSystem.js';
import type { CheckboxItem } from '../FileSelection/CheckboxGroup.jsx';
import { MobileFileSelectionContent } from '../FileSelection/MobileFileSelectionContent.jsx';
import { DesktopNodeSelectionContent } from './DesktopNodeSelectionContent.jsx';

type Props = {
  isDisabled: boolean;
  selectedFiles: string[];
  selectedFolders: string[];
  onSelectFiles: Dispatch<SetStateAction<string[]>>;
  onSelectFolders: Dispatch<SetStateAction<string[]>>;
  isMobile?: boolean;
};

const NodeSelection = ({
  isDisabled,
  selectedFiles,
  selectedFolders,
  onSelectFiles,
  onSelectFolders,
  isMobile = false,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const [isOpen, setIsOpen] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 1000);

  const handleChangeSearch = useCallback(({ target }: ChangeEvent) => {
    setSearchValue((target as HTMLInputElement).value);
  }, []);

  const handleClearSearch = useCallback(() => setSearchValue(''), []);

  const path = useAtomValue(folderPathAtom);
  const [attachPath, setAttachPath] = useState<FolderCrumb[]>(path);

  const { depth, currentFolderId, goToCrumb, enterFolder } =
    useFolderNavigation({
      path: attachPath,
      setPath: setAttachPath,
    });

  const {
    folders: availableFolders,
    files: availableFiles,
    isLoading: areFilesLoading,
  } = useFileSystemNodes({
    folderId: currentFolderId,
    depth,
    search: debouncedSearchValue,
  });

  const breadcrumbItems = useMemo(
    () => [{ folderId: ROOT_FOLDER_ID, name: t('My Files') }, ...attachPath],
    [attachPath, t],
  );

  const selectedFileNames = new Set(selectedFiles);
  const selectedFolderIds = new Set(selectedFolders);

  const files: Array<FileNode & CheckboxItem> = availableFiles
    .filter((f) => f.status === 'success')
    .map((f) => ({
      ...f,
      id: f.fileId,
      label: f.fileName,
      checked: selectedFileNames.has(f.fileName),
    }));

  const folders: Array<FolderNode & CheckboxItem> = availableFolders.map(
    (folder) => ({
      ...folder,
      // id included
      label: folder.name,
      checked: selectedFolderIds.has(folder.id), // Folders can have duplicate names
    }),
  );

  const handleItemChange = (
    label: string,
    checked: boolean,
    kind: FileSystemNode['kind'] = 'file',
  ) => {
    if (kind === 'folder') {
      onSelectFolders((prev) => {
        if (checked) {
          return [...prev, label];
        } else {
          return prev.filter((node) => node !== label);
        }
      });
    } else {
      onSelectFiles((prev) => {
        if (checked) {
          return [...prev, label];
        } else {
          return prev.filter((node) => node !== label);
        }
      });
    }
  };

  const handleSelectAll = () => {
    onSelectFiles(availableFiles.map((f) => f.fileName));
    onSelectFolders(availableFolders.map((f) => f.name));
  };

  const handleDeselectAll = () => {
    onSelectFiles([]);
    onSelectFolders([]);
  };

  const sharedProps = {
    isDisabled,
    isOpen,
    onOpenChange: setIsOpen,
    files,
    folders,
    areFilesLoading,
    onItemChange: handleItemChange,
    onSelectAll: handleSelectAll,
    onDeselectAll: handleDeselectAll,
    onOpenFolder: enterFolder,
    onCrumbNavigate: goToCrumb,
    breadcrumbItems,
    searchValue,
    handleChangeSearch,
    handleClearSearch,
  };

  if (isMobile) {
    return <MobileFileSelectionContent {...sharedProps} />;
  }

  return <DesktopNodeSelectionContent {...sharedProps} />;
};

export { NodeSelection };
